<template>
    <div
        class="engagement-ring-stone-recommendation-card"
        :class="{ 'highlighted': highlighted }"
    >
        <h4
            v-if="title"
            class="title"
        >
            {{ title }}
        </h4>

        <p
            v-if="subtitle"
            class="subtitle"
        >
            {{ subtitle }}
        </p>

        <StonePreview
            v-if="data.__typename !== 'StoneRecommendationNotFound'"
            :stone="data"
            class="stone-preview"
        >
            <template #button>
                <ThButton
                    v-qa="`select-recommendation`"
                    color="var(--blue)"
                    size="small"
                    type="solid"
                    @click="select"
                >
                    Select
                </ThButton>
            </template>
        </StonePreview>

        <div v-else-if="content">
            <div
                v-if="data.haveWithoutPricingRange"
                class="no-result"
            >
                <ContentComponentItem
                    v-for="(component, key) in content.notFoundContent"
                    :key
                    :background="'WHITE'"
                    :data="component"
                />
            </div>

            <div
                v-else
                class="no-result"
            >
                <ContentComponentItem
                    v-for="(component, key) in content.notSupportedContent"
                    :key
                    :background="'WHITE'"
                    :data="component"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
    EngagementRingStoneRecommendationCardFragment,
    StoneRecommendationsNotFoundFragment,
} from '@/graphql/api'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import {
    getEngagementRingStoneRecommendationCardSubtitle,
    getEngagementRingStoneRecommendationCardTitle,
} from './helpers'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import StonePreview from '@/components/stone/StonePreview.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'

const props = defineProps<{
    data: RingBuilderStoneTypes | StoneRecommendationsNotFoundFragment
    content: EngagementRingStoneRecommendationCardFragment | undefined
    highlighted?: boolean
}>()

const emit = defineEmits<{
    select: [stone: RingBuilderStoneTypes]
}>()

const select = () => {
    if (props.data.__typename !== 'StoneRecommendationNotFound')
        emit('select', props.data)
}

const title = computed(() =>
    getEngagementRingStoneRecommendationCardTitle(props.content, props.data)
)

const subtitle = computed(() =>
    getEngagementRingStoneRecommendationCardSubtitle(props.content, props.data)
)
</script>

<style lang="scss" scoped>
.engagement-ring-stone-recommendation-card {
    margin: 0 5px 80px;
    width: 100%;
    flex-shrink: 0;
    padding: 30px 0 15px;

    @include min-width(640) {
        margin: 0 5px;
        width: 33.33%;
    }

    &.highlighted {
        background: $stone-3;
        border: 1px solid $grey-200;
    }

    .title {
        text-align: center;
        font-family: $effra-font-stack;
        font-size: 0.9rem;
        line-height: 1em;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        font-weight: 400;
        margin: 0 0 10px;
    }

    .subtitle {
        min-height: 40px;
        text-align: center;
    }

    .no-result {
        min-height: 515px;
        background: $white;
        padding: 40px;
        border: 1px solid $grey-200;
        flex-shrink: 0;
        max-width: 340px;
        margin: 20px auto 0;
        text-align: center;
        display: flex;
        flex-wrap: wrap;
        align-content: center;
        align-items: center;
        justify-content: center;
    }

    .stone-preview {
        background: $white;
        border: 1px solid $grey-200;
        flex-shrink: 0;
        max-width: 340px;
        margin: 20px auto 0;
        cursor: initial;

        :deep(.image-wrapper) {
            padding: 8px;

            @include min-width(1024) {
                padding: 20px;
            }

            .image {
                object-fit: contain;
            }
        }

        :deep(.text-wrapper) {
            padding-top: 0;
        }
    }
}
</style>
